import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Crown, Shield, TrendingUp, Users, ArrowRight, Key } from 'lucide-react';
import SEO from '../../components/shared/SEO';
import { HexPattern } from '../../components/hive/HexMotif';

const HiveAccessPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('hive_token');
    const user = localStorage.getItem('hive_user');
    
    if (token && user) {
      navigate('/hive/dashboard');
    }
  }, [navigate]);

  const benefits = [
    { icon: Shield, title: 'Compliance Dashboard', desc: 'Track all deadlines and requirements' },
    { icon: TrendingUp, title: 'Revenue Analytics', desc: 'Monitor your performance metrics' },
    { icon: Users, title: 'Dispatch Support', desc: 'Access to your dedicated team' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="hive-access-page">
      <SEO 
        title="Enter The Hive - Queen B Logistics Portal"
        description="Access your Queen B Logistics carrier dashboard. Use your email and access code to enter."
      />

      {/* Hero Section */}
      <section className="relative py-20 border-b border-white/10">
        <HexPattern opacity={0.035} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <Crown className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-wider">The Hive</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-4">
              Your Command<br />
              <span className="text-[#D4AF37]">Center</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Access your personalized dashboard to manage compliance, track revenue, and coordinate with your dispatch team.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-[#121212] border border-white/10 p-6 text-center">
                <benefit.icon className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="text-white font-bold mb-1">{benefit.title}</h3>
                <p className="text-white/50 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Single Access Option */}
      <section className="py-16">
        <div className="max-w-lg mx-auto px-6">
          {/* Main Enter Button */}
          <div className="bg-[#121212] border-2 border-[#D4AF37]/50 p-8 text-center">
            <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
              <Key className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h2 className="text-white font-bold text-2xl mb-3">Enter the Hive</h2>
            <p className="text-white/60 mb-6">
              Use your email and access code from your welcome email to securely log in.
            </p>
            
            <Link
              to="/hive/login"
              className="flex items-center justify-center gap-3 w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
              data-testid="enter-hive-btn"
            >
              <Crown className="w-5 h-5" />
              Enter the Hive
              <ArrowRight className="w-5 h-5" />
            </Link>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-white/40 text-sm mb-3">Don't have an access code?</p>
              <Link
                to="/intake"
                className="text-[#D4AF37] hover:text-[#F5D47B] font-medium transition-colors"
              >
                Apply to Join Queen B →
              </Link>
            </div>
          </div>

          {/* How it works */}
          <div className="mt-8 bg-[#0D0D0D] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4 text-center">How It Works</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold">1</span>
                </div>
                <div>
                  <p className="text-white font-medium">Enter your email & access code</p>
                  <p className="text-white/50 text-sm">Found in your approval email</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold">2</span>
                </div>
                <div>
                  <p className="text-white font-medium">Check your email for a secure link</p>
                  <p className="text-white/50 text-sm">Link expires in 15 minutes</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold">3</span>
                </div>
                <div>
                  <p className="text-white font-medium">Click the link to enter</p>
                  <p className="text-white/50 text-sm">No password needed — simple & secure</p>
                </div>
              </div>
            </div>
          </div>

          {/* Admin Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/admin/login"
              className="text-white/30 hover:text-white/50 text-sm transition-colors"
            >
              Admin Portal →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HiveAccessPage;
